import { useState, useEffect } from 'react';
import { message } from 'antd';
import { generateId } from '../utils';

export const useTaskForm = (editingTask, onSubmit) => {
  const [formData, setFormData] = useState({
    content: '',
    assignee: '',
    description: '',
    priority: 'Medium',
    difficulty: 2,
    startDate: null,
    endDate: null,
    labels: [],
    attachments: [],
    subtasks: []
  });

  const [newSubtask, setNewSubtask] = useState('');
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionMode, setDescriptionMode] = useState('edit');

  // Initialize form data when editing task changes
  useEffect(() => {
    if (editingTask) {
      setFormData({
        content: editingTask.content || '',
        assignee: editingTask.assignee || '',
        description: editingTask.description || '',
        priority: editingTask.priority || 'Medium',
        difficulty: editingTask.difficulty || 2,
        startDate: editingTask.startDate || null,
        endDate: editingTask.endDate || null,
        labels: editingTask.labels || [],
        attachments: editingTask.attachments || [],
        subtasks: editingTask.subtasks || []
      });
    } else {
      // Reset form for new task
      setFormData({
        content: '',
        assignee: '',
        description: '',
        priority: 'Medium',
        difficulty: 2,
        startDate: null,
        endDate: null,
        labels: [],
        attachments: [],
        subtasks: []
      });
      setNewSubtask('');
      setDescriptionExpanded(false);
      setDescriptionMode('edit');
    }
  }, [editingTask]);

  // Form field handlers
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateDateRange = (dates) => {
    setFormData(prev => ({
      ...prev,
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }));
  };

  // Subtask handlers
  const addSubtask = () => {
    if (newSubtask.trim()) {
      const subtask = {
        id: generateId('subtask-'),
        content: newSubtask.trim(),
        completed: false,
        createdAt: new Date().toISOString()
      };
      setFormData(prev => ({
        ...prev,
        subtasks: [...prev.subtasks, subtask]
      }));
      setNewSubtask('');
      message.success('Subtask added successfully!');
    }
  };

  const deleteSubtask = (subtaskId) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter(subtask => subtask.id !== subtaskId)
    }));
    message.success('Subtask deleted!');
  };

  const toggleSubtask = (subtaskId) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.map(subtask => 
        subtask.id === subtaskId 
          ? { 
              ...subtask, 
              completed: !subtask.completed,
              completedAt: !subtask.completed ? new Date().toISOString() : null
            }
          : subtask
      )
    }));
  };

  const editSubtask = (subtaskId, newContent) => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.map(subtask => 
        subtask.id === subtaskId 
          ? { ...subtask, content: newContent }
          : subtask
      )
    }));
  };

  const clearCompletedSubtasks = () => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.filter(subtask => !subtask.completed)
    }));
    message.success('Completed subtasks cleared!');
  };

  const markAllSubtasksComplete = () => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.map(subtask => ({ 
        ...subtask, 
        completed: true,
        completedAt: new Date().toISOString()
      }))
    }));
    message.success('All subtasks marked as complete!');
  };

  const markAllSubtasksIncomplete = () => {
    setFormData(prev => ({
      ...prev,
      subtasks: prev.subtasks.map(subtask => ({ 
        ...subtask, 
        completed: false,
        completedAt: null
      }))
    }));
    message.success('All subtasks marked as incomplete!');
  };

  // File attachment handlers
  const addAttachment = (file) => {
    const newFile = {
      id: generateId('file-'),
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      type: file.type || 'file',
      uploadDate: new Date().toISOString()
    };
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, newFile]
    }));
    message.success(`${file.name} file uploaded successfully`);
  };

  const removeAttachment = (fileId) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(file => file.id !== fileId)
    }));
  };

  // Description handlers
  const toggleDescriptionMode = () => {
    setDescriptionMode(prev => prev === 'edit' ? 'preview' : 'edit');
  };

  const toggleDescriptionExpanded = () => {
    setDescriptionExpanded(prev => !prev);
  };

  // Form submission
  const handleSubmit = () => {
    const taskData = {
      ...formData,
      id: editingTask?.id || generateId('task-'),
      createdAt: editingTask?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSubmit(taskData);
  };

  return {
    formData,
    newSubtask,
    setNewSubtask,
    descriptionExpanded,
    descriptionMode,
    updateField,
    updateDateRange,
    addSubtask,
    deleteSubtask,
    toggleSubtask,
    editSubtask,
    clearCompletedSubtasks,
    markAllSubtasksComplete,
    markAllSubtasksIncomplete,
    addAttachment,
    removeAttachment,
    toggleDescriptionMode,
    toggleDescriptionExpanded,
    handleSubmit
  };
};
